/**
 * Nether: cart drawer behavior layer.
 *
 * 1) Scroll depth shadows: toggles data-nether-shadow-top / data-nether-shadow-bottom
 *    on the drawer dialog so the header casts a shadow over scrolled items and the
 *    summary casts one upward when more content sits below (the Gymshark
 *    sheet-within-sheet cue). Replaces Horizon's scroll-hint mask fades, which are
 *    killed in nether-cart-styles — the mask made the bottom 10% of the scroller
 *    transparent, which both read as a "white fade" and hid the bottom of the last
 *    line item (stepper / bundle label) on short iOS sheets.
 * 2) Minus-to-zero: there is no trash icon; at the minimum quantity the stepper's
 *    minus removes the line and shows a black Undo toast (thumb + message + Undo).
 *    Undo re-adds through Shopify.actions.updateCart so the drawer morphs natively.
 *    Emptying the bag closes the sheet first, so its toast floats on the page
 *    instead of inside the dialog and its Undo reopens the drawer.
 * 3) A rejected discount code keeps what was typed: stock cart-discount.js blanks
 *    the field before it shows the error, so a typo cost you the whole code.
 */

const DRAWER = '#cart-drawer';
const SECTION_ID = 'cart-drawer-section';
const TOAST_MS = 2000;

/* ------------------------------------------------------------------ */
/* Close watchdog: never let the drawer get stranded half-closed        */
/* ------------------------------------------------------------------ */
/* Horizon finishes closing on the slide-out's animationend. If that event
   never arrives the dialog keeps `open` AND the `--closing` class, so the
   cart looks open but is mid-dismissal and the X appears dead — the
   "tried to close the cart summary and it didn't close" report (07-26),
   reproduced here with the dialog stuck in theme-drawer__dialog--closing.
   Animations can fail to fire for several reasons (an interrupted frame,
   a section morph landing mid-transition, reduced-motion setups), so do
   not rely on the event alone: if the class is still there after the
   animation should have finished, finish the job. */
const CLOSE_WATCHDOG_MS = 900;
let closeWatchdog = null;

function watchDrawerClosing() {
  const dialog = getDialog();
  if (!dialog) return;

  const observer = new MutationObserver(() => {
    const closing = dialog.classList.contains('theme-drawer__dialog--closing');
    if (!closing) {
      clearTimeout(closeWatchdog);
      return;
    }
    clearTimeout(closeWatchdog);
    closeWatchdog = setTimeout(() => {
      if (!dialog.classList.contains('theme-drawer__dialog--closing')) return;
      dialog.classList.remove('theme-drawer__dialog--closing');
      dialog.removeAttribute('open');
      if (typeof dialog.close === 'function' && dialog.open) {
        try {
          dialog.close();
        } catch (error) {
          /* already closed */
        }
      }
      document.querySelector(DRAWER)?.removeAttribute('open');
    }, CLOSE_WATCHDOG_MS);
  });

  observer.observe(dialog, { attributes: true, attributeFilter: ['class'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchDrawerClosing);
} else {
  watchDrawerClosing();
}

/* The free-shipping tooltip handler used to live here. Its markup
   (.nether-fsb__tip and .nether-fsb__tip-btn) was removed when the note moved
   into the delivery-and-returns disclosure, so the handler matched nothing and
   ran a querySelectorAll on every click in the document. Gone with it. */

/** @returns {HTMLElement | null} */
function getDialog() {
  return document.querySelector(`${DRAWER} .theme-drawer__dialog`);
}

/* ------------------------------------------------------------------ */
/* Scroll shadows                                                      */
/* ------------------------------------------------------------------ */

let shadowRaf = 0;

function scheduleShadowUpdate() {
  if (shadowRaf) return;
  shadowRaf = requestAnimationFrame(() => {
    shadowRaf = 0;
    updateShadows();
  });
}

function updateShadows() {
  const dialog = getDialog();
  if (!dialog) return;

  /* Both candidates are listed because WHICH ONE scrolls changes with the
     breakpoint: .cart-drawer__items is the scroller on desktop, and below 750px
     nether-cart-styles.liquid:526 gives it `overflow: visible` and hands the job
     to .cart-drawer__content.

     The overflow test is not decoration. `scrollHeight > clientHeight` is true
     for a NON-scrolling box too — descendant margin boxes count toward the
     scrolling area — while its scrollTop stays pinned at 0 forever. That box
     therefore contributed `scrollTop > 2` = false (harmless, which is why the
     top edge always looked right) but `scrollTop + clientHeight < scrollHeight`
     = true permanently, so the results being OR-ed together meant the bottom
     shadow could never clear on mobile no matter how far down you scrolled.
     Asking for the computed overflow first is what separates a real scroller
     from a tall static box. */
  const scrollers = [
    dialog.querySelector('.cart-drawer__content'),
    dialog.querySelector('.cart-drawer__items'),
  ].filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll') return false;
    return el.scrollHeight > el.clientHeight + 1;
  });

  let top = false;
  let bottom = false;
  for (const el of scrollers) {
    if (el.scrollTop > 2) top = true;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 2) bottom = true;
  }

  dialog.toggleAttribute('data-nether-shadow-top', top);
  dialog.toggleAttribute('data-nether-shadow-bottom', bottom);
}

/* ------------------------------------------------------------------ */
/* Let the drawer size itself                                          */
/* ------------------------------------------------------------------ */
/* The totals are wherever the content puts them. One item and they are simply
   on screen; a full cart and they fall below the fold on their own.

   This used to FORCE them under the fold by padding the items block up to the
   viewport height, on the reasoning that products should own the first screen.
   That was overcorrecting: it manufactured a scroll for a two-line cart and left
   an empty gap, and the "right" viewport height is not knowable anyway — Safari's
   collapsing toolbar means it differs between a phone and a desktop browser at
   the same nominal size (user 07-27: "if you don't need to scroll to the order
   summary, then you shouldn't have to... it should be there dynamically").

   Any inline min-height left over from that behaviour is cleared here, since a
   morph can carry one back in. */
function sizeCartItemsToViewport() {
  const dialog = getDialog();
  if (!dialog) return;

  const items = dialog.querySelector('.cart-drawer__items');
  if (items && items.style.minHeight) items.style.minHeight = '';
}

function scheduleItemsSizing() {
  requestAnimationFrame(sizeCartItemsToViewport);
}

/* ------------------------------------------------------------------ */
/* Quantity is stepper-only — tapping the number must not open a keyboard */
/* ------------------------------------------------------------------ */
/* Tapping the quantity in the cart summary focused the field and raised the
   iOS keyboard, which is never what someone reaching for + or - wants (user
   07-26). readonly blocks the keyboard and typing but still allows the theme
   to set .value programmatically, so every stepper update keeps working —
   unlike `disabled`, which would drop the field from the form submission.
   inputmode="none" is belt and braces for engines that still offer a keypad. */
function lockQuantityInputs(root) {
  (root || document).querySelectorAll('input[name="updates[]"]').forEach((input) => {
    if (input.readOnly) return;
    input.readOnly = true;
    input.setAttribute('inputmode', 'none');
  });
}

/* ------------------------------------------------------------------ */
/* iOS body lock: overflow:hidden does not stop touch scrolling on iOS  */
/* Safari, so the page kept moving behind the open sheet (user 07-25).  */
/* Freeze the body in place while the cart is open, restore on close.   */
/* Mobile only — desktop scrolls via .page-wrapper and never chained.   */
/* ------------------------------------------------------------------ */

let lockScrollY = 0;

function syncBodyLock() {
  const dialog = getDialog();
  const shouldLock =
    !!dialog && dialog.hasAttribute('open') && window.matchMedia('(max-width: 749px)').matches;
  const body = document.body;
  const locked = body.hasAttribute('data-nether-cart-lock');
  if (shouldLock && !locked) {
    lockScrollY = window.scrollY;
    body.setAttribute('data-nether-cart-lock', '');
    body.style.position = 'fixed';
    body.style.top = `${-lockScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  } else if (!shouldLock && locked) {
    body.removeAttribute('data-nether-cart-lock');
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, lockScrollY);
  }
}

function initShadows() {
  const drawer = document.querySelector(DRAWER);
  if (!drawer) return;

  // One capture listener catches scrolls on whichever inner container scrolls
  // (content on the mobile sheet, items on desktop).
  drawer.addEventListener('scroll', scheduleShadowUpdate, { capture: true, passive: true });
  window.addEventListener('resize', scheduleShadowUpdate, { passive: true });

  // Re-measure when the usable height actually changes: rotation, and iOS
  // Safari showing/hiding its toolbar (which fires resize, not orientationchange).
  window.addEventListener('resize', scheduleItemsSizing, { passive: true });
  window.addEventListener('orientationchange', scheduleItemsSizing, { passive: true });

  // Recompute after morphs (add / remove / qty) and when the dialog opens —
  // morphs also wipe the dialog attributes, so this restores them. The same
  // observer keeps the iOS body lock in sync with the open state.
  const observer = new MutationObserver(() => {
    scheduleShadowUpdate();
    syncBodyLock();
    // Morphs (add / remove / qty) rebuild the items block and drop the inline
    // min-height with it, so re-apply on every one, not just on open.
    scheduleItemsSizing();
    // Morphs also bring back fresh, editable quantity fields.
    lockQuantityInputs();
    // Section morphs (the removal re-render) replace the dialog's children and
    // take the toast with them — the "lag" the user saw was the toast getting
    // wiped mid-entrance. Re-attach it while its timer is still running.
    // The floating card is on <body>, out of the morph's reach, and must never
    // be pulled into the dialog: it is only up because the dialog is closed.
    if (toastEl && !toastEl.isConnected && !toastEl.classList.contains('nether-cart-toast--floating')) {
      const dialog = getDialog();
      if (dialog && dialog.hasAttribute('open')) dialog.appendChild(toastEl);
    }
    // The dialog opens with showModal(), which promotes it to the top layer
    // above any z-index, so a floating card left up while the bag is reopened
    // would sit there invisible with a live timer. Retire it instead.
    /* GRACE PERIOD, or this never fires for the user at all.
       Measured before this: the toast mounted at 1053ms and was retired at
       1055ms. Two milliseconds. The Undo button was never clickable.

       The reason is sequencing, not intent. A floating toast is raised while
       the drawer is CLOSING, and the dialog's open attribute is still present
       for a frame or two after the close begins. This observer fires on that
       same mutation, sees floating-toast-plus-open-dialog, and reads a state
       that is a millisecond old as the bad state it was written to catch.

       The state it was written to catch is real: showModal() promotes the
       dialog into the top layer above any z-index, so a floating card left up
       while the bag is REOPENED sits there invisible with a live timer. That
       only happens when the dialog opens after the toast, which is exactly what
       the grace period distinguishes. */
    const born = toastEl ? Number(toastEl.dataset.born || 0) : 0;
    const settled = born && Date.now() - born > 400;
    if (
      toastEl &&
      settled &&
      toastEl.classList.contains('nether-cart-toast--floating') &&
      getDialog()?.hasAttribute('open')
    ) {
      dismissToast();
    }
  });
  observer.observe(drawer, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });

  scheduleShadowUpdate();
  syncBodyLock();
  scheduleItemsSizing();
  lockQuantityInputs();
}

/* ------------------------------------------------------------------ */
/* A rejected discount code keeps what you typed                       */
/* ------------------------------------------------------------------ */
/* Stock assets/cart-discount.js blanks input[name="discount"] on BOTH rejection
   branches — not-applicable (line 92) and shipping-only (line 119) — before it
   un-hides the error. So one mistyped character costs the whole code, and the
   field you have to retype it into is the only thing on screen that still looks
   fine. Typing a code wrong should not cost you the code.

   Wrapped here rather than edited there: cart-discount.js is stock Horizon and
   this drawer has been bitten before by diverging from it.

   applyDiscount is an instance FIELD (an arrow function assigned in the
   constructor), not a prototype method — ctor.prototype.applyDiscount is
   undefined — so the patch has to land on each element rather than on the
   prototype. component.js resolves the handler off the instance at event time
   (`instance[method]`, component.js:307), so reassigning the property is enough
   for the declarative on:submit to pick it up. connectedCallback carries it to
   the instances a section morph creates, which matters because the component
   only exists while the cart has items. */

/** @type {WeakSet<Element>} */
const patchedDiscountComponents = new WeakSet();

const DISCOUNT_ERROR_REFS =
  '[ref="cartDiscountError"], [ref="cartDiscountErrorDiscountCode"], [ref="cartDiscountErrorShipping"]';

/** @param {Element} component - a <cart-discount-component> */
function keepRejectedDiscountCode(component) {
  if (!(component instanceof HTMLElement) || patchedDiscountComponents.has(component)) return;

  const original = /** @type {any} */ (component).applyDiscount;
  if (typeof original !== 'function') return;
  patchedDiscountComponents.add(component);

  /** @type {any} */ (component).applyDiscount = async (/** @type {SubmitEvent} */ event) => {
    const form = event.target;
    const input = form instanceof HTMLElement ? form.querySelector('input[name="discount"]') : null;
    const typed = input instanceof HTMLInputElement ? input.value : '';

    clearDiscountRejection(component);
    await original(event);

    // The stock handler un-hides the wrapper for either rejection and leaves it
    // hidden on success, so its state is the answer to "was this refused?".
    const error = component.querySelector('[ref="cartDiscountError"]');
    if (!error || error.classList.contains('hidden')) return;
    if (!(input instanceof HTMLInputElement) || !typed) return;

    input.value = typed;
    // What .nether-csum__discount-input[data-invalid] in
    // nether-cart-summary.liquid has been waiting for: until this line the rule
    // was written but nothing ever set the attribute, so a refused code was
    // explained by the line underneath and contradicted by the field itself.
    input.setAttribute('data-invalid', '');
    input.setAttribute('aria-invalid', 'true');
    // Focus goes back to the field, not the Apply button they just pressed:
    // the fix is a character or two, and the caret should already be there.
    input.focus({ preventScroll: true });
    try {
      input.setSelectionRange(typed.length, typed.length);
    } catch {
      /* selection is not supported on every input type */
    }
  };
}

/* Editing the code retires the rejection. A red border and "That code is not
   valid." sitting under a value the shopper has since corrected is a statement
   about the old code being made about the new one. */
/** @param {Element} component */
function clearDiscountRejection(component) {
  const input = component.querySelector('input[name="discount"]');
  if (input instanceof HTMLInputElement) {
    input.removeAttribute('data-invalid');
    input.removeAttribute('aria-invalid');
  }
  component.querySelectorAll(DISCOUNT_ERROR_REFS).forEach((el) => el.classList.add('hidden'));
}

// One document listener rather than one per instance, so morph-created fields
// are covered without rebinding. The attribute test short-circuits every other
// keystroke on the page before any DOM walking.
document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== 'discount') return;
  if (!target.hasAttribute('data-invalid')) return;
  const component = target.closest('cart-discount-component');
  if (component) clearDiscountRejection(component);
});

customElements.whenDefined('cart-discount-component').then((ctor) => {
  const originalConnected = ctor.prototype.connectedCallback;
  ctor.prototype.connectedCallback = function () {
    // Patch first: it only needs the constructor's instance field, and this way
    // a throw inside the stock callback cannot leave an unpatched form behind.
    keepRejectedDiscountCode(this);
    originalConnected?.call(this);
  };

  document.querySelectorAll('cart-discount-component').forEach(keepRejectedDiscountCode);
});

/* ------------------------------------------------------------------ */
/* Minus-to-zero + Undo toast                                          */
/* ------------------------------------------------------------------ */

// The stock cart stepper disables minus at the minimum quantity; re-enable it in
// the drawer so a tap at min can remove the line (the no-trash pattern), and
// intercept decreaseQuantity itself so the tap routes to removal. Patching the
// method (not a click listener) matters: the theme's declarative events run from
// a document-level capture listener registered before this module, so a separate
// click handler would only see the input AFTER the stock code already clamped and
// dispatched a same-value update — racing the removal with a stale line-number
// POST. Inside the method there is no race: at min we remove and return before
// the stock dispatch.
customElements.whenDefined('cart-quantity-selector-component').then((ctor) => {
  const originalUpdateButtonStates = ctor.prototype.updateButtonStates;
  ctor.prototype.updateButtonStates = function () {
    originalUpdateButtonStates.call(this);
    if (!this.closest(DRAWER)) return;
    const minus = this.refs?.minusButton;
    const input = this.refs?.quantityInput;
    if (minus && input && !input.disabled) minus.disabled = false;
  };

  const originalDecreaseQuantity = ctor.prototype.decreaseQuantity;
  ctor.prototype.decreaseQuantity = function (event) {
    if (this.closest(DRAWER)) {
      const { min, value } = this.getCurrentValues?.() ?? {};
      if (typeof value === 'number' && typeof min === 'number' && value <= min) {
        event.preventDefault();
        removeLine(this);
        return;
      }
    }
    originalDecreaseQuantity.call(this, event);
  };

  document
    .querySelectorAll(`${DRAWER} cart-quantity-selector-component`)
    .forEach((el) => el.updateButtonStates?.());
});

/* The trash gets the same Undo as the minus (bug hunt 2026-10-22). Its on:click called the stock
   onLineItemRemove directly, so a trash removal skipped removeLine(): no Undo card, and for the last line
   no close-first sequence. A window capture listener runs before the theme's document-level declarative
   listener, so the tap is routed through removeLine() instead. */
window.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest(`${DRAWER} .cart-items__remove`);
    if (!(button instanceof HTMLButtonElement) || button.hidden || button.disabled) return;
    const component = button.closest('cart-items-component');
    if (!button.closest('.cart-items__table-row') || !component || typeof component.onLineItemRemove !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeLine(button);
  },
  true
);

/** Until when a last-line removal is in flight (see removeLine). */
let emptyingUntil = 0;

/** @param {Element} el - any element inside the row */
function removeLine(el) {
  const row = el.closest('.cart-items__table-row');
  const component = el.closest('cart-items-component');
  if (!(row instanceof HTMLElement) || !component) return;

  const rows =
    component.refs?.cartItemRows ?? Array.from(component.querySelectorAll('[ref="cartItemRows[]"]'));
  const line = rows.indexOf(row) + 1;
  if (line === 0 || typeof component.onLineItemRemove !== 'function') return;

  // Bundle parents (rows with nested child lines) can't be faithfully re-added
  // from the drawer — remove without offering Undo.
  const key = row.dataset.key;
  const children = rows.filter((r) => r !== row && key && r.dataset.parentKey === key);
  const hasChildren = children.length > 0;

  const isLastItem = rows.length - children.length <= 1;

  // A line that can no longer be bought (a colour taken off sale while it sat in
  // the bag, e.g. Abyss 2026-10-18) gets no Undo either: Shopify would refuse to
  // re-add it, and the toast promised something that could not happen.
  const unavailable = row.classList.contains('cart-items__table-row--unavailable');

  const payload = hasChildren || unavailable
    ? null
    : {
        id: row.dataset.netherVariantId || '',
        quantity: parseInt(row.dataset.netherQuantity || '1', 10) || 1,
        properties: safeParseJson(row.dataset.netherProperties),
        sellingPlan: row.dataset.netherSellingPlan || '',
        image: row.dataset.netherImage || '',
      };

  if (isLastItem) {
    // One emptying at a time (2026-10-05). The minus stays live for the 700ms
    // below, so a second tap used to queue a second close and a second removal
    // of a line that was already gone.
    if (Date.now() < emptyingUntil) return;
    emptyingUntil = Date.now() + 1600;

    // Removing the last line: don't morph to the empty state in view (the
    // in-place swap read as a glitch on device) — the sheet slides down first,
    // then the removal runs behind the closed drawer.
    //
    // That sequencing used to mean the one removal nobody can walk back was
    // also the only one with no Undo — every other quantity change is
    // recoverable, and emptying the bag, the change that costs the most, was
    // not. The card is the same card; it just parks on the page, because by the
    // time it appears the sheet it used to live in has gone. Its Undo reopens
    // the drawer, since closing was part of what it is undoing.
    //
    // Toast AFTER the removal fires, not alongside the close, so the undo
    // window starts when the bag is actually empty rather than 700ms earlier.
    dismissToast();
    const drawer = document.getElementById('cart-drawer');
    setTimeout(() => {
      if (drawer && typeof drawer.close === 'function') drawer.close();
      else getDialog()?.close();
    }, 140);
    setTimeout(() => {
      component.onLineItemRemove(line);
      if (payload && payload.id) showToast(payload, { floating: true });
    }, 700);
    return;
  }

  component.onLineItemRemove(line);
  if (payload && payload.id) showToast(payload);
}

/**
 * How much of the bottom of the viewport is already occupied by something
 * pinned there. The floating toast sits above it rather than covering it, which
 * is the same rule the in-drawer toast follows for Checkout.
 *
 * Usually this returns 0 — the PDP sticky add-to-cart bar, the obvious
 * candidate, has already slid away by the time the toast appears, because
 * opening the cart drawer hides it (measured: the bar was off-screen at
 * opacity 0 when the toast was built). This is the guard for the cases where
 * something IS parked there, verified with an 80px fixed strip: offset came
 * back 94px and the card cleared it by exactly the 14px gap.
 *
 * Measured rather than keyed to a class name, so it stays true if that bar is
 * renamed or another one is added. One pass over the document, ~4ms measured on
 * the PDP (the heaviest template), and only on the removal that empties the bag.
 * Elements taller than a third of the viewport are skipped: those are scrims and
 * sheets, not bars.
 *
 * @returns {number} pixels of the viewport bottom to stay clear of
 */
function bottomClearance() {
  const viewport = window.innerHeight;
  let highest = viewport;

  for (const el of document.body.querySelectorAll('*')) {
    // Not the card itself (bug hunt 2026-10-22): it is fixed too, so the 250ms re-measure counted it as
    // something parked at the bottom, lifted it by its own height, then dropped it back at 650ms.
    if (el.classList.contains('nether-cart-toast')) continue;
    const styles = getComputedStyle(el);
    if (styles.position !== 'fixed') continue;
    if (styles.visibility === 'hidden' || parseFloat(styles.opacity) < 0.05) continue;

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    if (rect.height > viewport / 3) continue;
    // Only things actually parked at the bottom edge.
    if (rect.bottom < viewport - 80) continue;

    highest = Math.min(highest, rect.top);
  }

  return Math.max(0, Math.round(viewport - highest));
}

/** @param {string | undefined} raw */
function safeParseJson(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** @type {HTMLElement | null} */
let toastEl = null;
let toastTimer = 0;

/**
 * @param {{ id: string, quantity: number, properties: object | null, sellingPlan: string, image: string }} payload
 * @param {{ floating?: boolean }} [options] - floating: park the card on the
 *   page rather than inside the dialog, for the removal that closes the drawer.
 */
function showToast(payload, options = {}) {
  const floating = options.floating === true;
  const dialog = getDialog();
  const host = floating ? document.body : dialog;
  if (!host) return;
  dismissToast();

  // In the drawer: sit just above the summary block so it never covers
  // Checkout. On the page: sit above whatever is already pinned to the bottom
  // of the viewport, same rule, different obstacle.
  let offset = 24;
  if (floating) {
    offset = bottomClearance() + 14;
  } else if (dialog) {
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (summary) offset = Math.round(summary.getBoundingClientRect().height) + 14;
  }

  const toast = document.createElement('div');
  toast.className = floating ? 'nether-cart-toast nether-cart-toast--floating' : 'nether-cart-toast';
  toast.setAttribute('role', 'status');
  toast.style.setProperty('--nether-toast-offset', `${offset}px`);

  if (payload.image) {
    const img = document.createElement('img');
    img.className = 'nether-cart-toast__img';
    img.src = payload.image;
    img.alt = '';
    img.width = 44;
    img.height = 44;
    toast.appendChild(img);
  }

  const text = document.createElement('span');
  text.className = 'nether-cart-toast__txt';
  text.textContent = 'You removed an item';
  toast.appendChild(text);

  const undoButton = document.createElement('button');
  undoButton.type = 'button';
  undoButton.className = 'nether-cart-toast__undo';
  undoButton.textContent = 'Undo';
  // Reopening is only right for the floating card: that removal closed the
  // drawer, so putting the item back without the bag would leave nothing on
  // screen to say it worked.
  undoButton.addEventListener('click', () => undo(payload, { reopenDrawer: floating }));
  toast.appendChild(undoButton);

  host.appendChild(toast);
  toastEl = toast;

  /* RE-MEASURE THE CLEARANCE AFTER THE DRAWER FINISHES CLOSING.
     Floating card only — in the drawer the obstacle is the summary block, which
     is already settled when this runs.

     The one measurement above is taken at the worst possible instant. Removing
     the last line closes the drawer, and the close animation is still running
     when the card is built, so the PDP's sticky add-to-cart bar is mid-transition
     and bottomClearance() reads 0. The card mounts flush to the bottom and the
     sticky bar then settles on top of it, burying Undo — on the one removal that
     most needs an undo, because it emptied the bag.

     Re-running it once the close has settled is what puts the card above the bar.
     Two passes rather than a timer guess: ~250ms catches a fast close, ~650ms
     covers the full animation plus the sticky bar's own transition. Both are
     cheap, both no-op when the value has not moved, and both bail if the card was
     dismissed or replaced in the meantime. */
  if (floating) {
    [250, 650].forEach((delay) => {
      setTimeout(() => {
        if (toastEl !== toast || !toast.isConnected) return;
        const settled = bottomClearance() + 14;
        if (settled !== offset) {
          offset = settled;
          toast.style.setProperty('--nether-toast-offset', `${settled}px`);
        }
      }, delay);
    });
  }
  /* Birth stamp, read by the top-layer guard in the drawer observer. See the
     comment there for why a floating toast needs a grace period. */
  toast.dataset.born = String(Date.now());
  toastTimer = window.setTimeout(dismissToast, TOAST_MS);
}

function dismissToast() {
  clearTimeout(toastTimer);
  const el = toastEl;
  toastEl = null;
  if (!el) return;
  el.classList.add('is-leaving');
  setTimeout(() => el.remove(), 260);
}

/**
 * @param {{ id: string, quantity: number, properties: object | null, sellingPlan: string }} payload
 * @param {{ reopenDrawer?: boolean }} [options]
 */
async function undo(payload, options = {}) {
  dismissToast();

  // Only after the cart actually holds the item again, so the sheet never
  // slides back up showing the empty state it is about to replace.
  const reopenDrawer = () => {
    if (!options.reopenDrawer) return;
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (typeof drawer?.open === 'function') drawer.open();
  };

  try {
    const hasProps = payload.properties && Object.keys(payload.properties).length > 0;
    const actions = window.Shopify?.actions;

    if (!hasProps && !payload.sellingPlan && actions && typeof actions.updateCart === 'function') {
      // Platform Standard Action: emits shopify:cart:lines-update, the drawer
      // morphs itself (same path as the upsell quick-add).
      await actions.updateCart({
        lines: [{ merchandiseId: String(payload.id), quantity: payload.quantity }],
      });
      reopenDrawer();
      return;
    }

    // Items with line properties / selling plans need the Cart API to carry them.
    const item = { id: Number(payload.id), quantity: payload.quantity };
    if (hasProps) item.properties = payload.properties;
    if (payload.sellingPlan) item.selling_plan = Number(payload.sellingPlan);

    const response = await fetch(`${Theme.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

    const { sectionRenderer } = await import('@theme/section-renderer');
    await sectionRenderer.renderSection(SECTION_ID, { cache: false, mode: 'hydration' });
    reopenDrawer();
  } catch (error) {
    console.error('[nether-cart] undo', error);
  }
}

initShadows();

/* ANDROID BACK GESTURE (2026-10-05). Chrome on Android closes an open modal <dialog> on
 * the system back gesture: a close request, with no keydown. theme-drawer.js only
 * listens for the Escape key, so the dialog vanished while <theme-drawer open> and
 * html[scroll-lock] stayed behind. The page could not scroll, the bag icon needed
 * two taps (the first only cleared the leftover state), and anything that opens
 * the bag in code silently did nothing. The request is routed through the
 * drawer's own close() instead, which animates it shut and clears its state.
 *
 * cancel is the close request; preventing it keeps the dialog for the drawer to
 * animate. Chrome only lets a page prevent it after a user gesture, so close
 * catches the case where the dialog shut anyway. The drawer's own close() runs
 * panel.close() only after it has removed [open], so it never re-enters here.
 * Both events do not bubble: capture on document sees them. */
document.addEventListener(
  'cancel',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const drawer = dialog.closest('theme-drawer');
    if (!drawer || typeof drawer.close !== 'function' || !drawer.hasAttribute('open')) return;
    event.preventDefault();
    drawer.close();
  },
  true
);
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    // theme-drawer's #bringToFront() re-stacks an open drawer with close() then
    // showModal(), so its close event arrives with the dialog already open
    // again. That is not a dismissal; closing here shut the bag Undo had just
    // reopened (caught in testing 2026-10-05).
    if (dialog.open) return;
    const drawer = dialog.closest('theme-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
  },
  true
);
